<template>
  <div style="width: 100%">
    <slot />

    <div class="management-section-wrapper">
      <v-card-title>
        <span class="management-section-title">{{ $tc("Role", 2) }}</span>
        <v-spacer />
        <v-text-field
          v-if="canSeeTable"
          :value="searchFromParent"
          @input="updateSearchInParent"
          append-icon="search"
          :label="$t('Search')"
          :placeholder="$t('Search')"
          class="data-table-search"
          single-line
          hide-details
        />
      </v-card-title>

      <v-data-table
        v-if="canSeeTable"
        :headers="headers"
        :items="roles"
        :search="searchFromParent"
        :loading="loading"
        loading-text=""
        :page.sync="pagination.page"
        :items-per-page.sync="paginationRole.rowsPerPage"
        :items-per-page-text="rowsPerPageText"
        :items-per-page-items="rowsPerPageItems"
        :header-props="{ sortIcon }"
        :footer-props="{ prevIcon, nextIcon, 'items-per-page-options':rowsPerPageItems }"
        :class="{
          'no-show-permission': !userHasPermissionTo('show')
        }"
        @pagination="setRowsOnLocalStorage(dataTableRoleListId,paginationRole.rowsPerPage)"
      >
        <template v-slot:item="props">
          <tr
            :class="{ 'cursor-pointer': userHasPermissionTo('show') }"
            @click="viewItem(props.item)"
          >
            <td>{{ props.item.label }}</td>

            <td>{{ props.item.description }}</td>

            <td>
              <div
                class="user-status-icon"
                :class="{ 'status-inactive': !props.item.active }"
              />
              <span>
                {{ props.item.active ? $tc("Active", 1) : $tc("Inactive", 1) }}
              </span>
            </td>

            <td>
              <div
                class="user-status-icon"
                :class="{ 'status-inactive': !props.item.visible }"
              />
              <span>
                {{ props.item.visible ? $t("Visible") : $t("NotVisible") }}
              </span>
            </td>

            <td>
              <app-datatable-action-buttons
                :user-can-edit="userHasPermissionTo('update')"
                @edit="editItem(props.item)"
                :user-can-delete="
                  !props.item.blocked && userHasPermissionTo('destroy')
                "
                @delete="askConfirmationToDeleteItem(props.item)"
              />
            </td>
          </tr>
        </template>
        <template v-slot:pageText="props">
          {{ props.pageStart }} - {{ props.pageStop }} {{ $t("tables.Of") }}
          {{ props.itemsLength }}
        </template>

        <template v-slot:no-results>
          <data-table-no-results :search="searchFromParent" />
        </template>

        <template v-slot:no-data>
          <div v-if="loading" />
          <v-alert v-else class="data-table-no-data" icon="warning">
            {{ $t("NoDataRoles") }}
          </v-alert>
        </template>
      </v-data-table>

      <app-no-permission-warning v-if="!canSeeTable" />
    </div>

    <app-dialog-box
      :open="deleteDialog"
      :title="$t('DeleteRole')"
      :content="$t('DeleteConfirmation') + ' ' + itemToDelete.label + '?'"
      :main-action-label="$t('No')"
      @mainAction="cancelDelete"
      :secondary-action-label="$t('Yes')"
      @secondaryAction="deleteItem"
      :loading="loadingDelete"
    />
  </div>
</template>

<script>
import { mapActions } from "vuex";
import { managementDatatableMixin } from "@/mixins/management/managementDatatableMixin";

import AppDatatableActionButtons from "@/components/_Molecules/AppDatatableActionButtons";
import DataTableNoResults from "@/components/_Atoms/DataTableNoResults";
import AppDialogBox from "@/components/_Molecules/AppDialogBox";
import LockSVG from "@/assets/management/lock.svg";
import AppNoPermissionWarning from "@/components/_Molecules/AppNoPermissionWarning";
export default {
  name: "RoleList",

  components: {
    AppNoPermissionWarning,
    AppDialogBox,
    DataTableNoResults,
    AppDatatableActionButtons,
    LockSVG
  },

  mixins: [managementDatatableMixin],

  data() {
    return {
      /**
       * List of client roles
       */
      roles: [],

      /**
       * Permission name referring to the functionality of client roles
       */
      permissionName: "client_role",
      
      dataTableRoleListId:"dataTableRoleListId",

      paginationRole: {
        rowsPerPage: this.getRowsFromLocalStorage("dataTableRoleListId")
      },
      
      rowsPerPageOptions: [15, 50, 100, -1],

      /**
       * Sets the loading of the delete a client role dialog button
       */
      loadingDelete: false,

      /**
       * Defines the loading of the client roles table
       */
      loading: true
    };
  },

  computed: {
    /**
     * Determines whether the user can edit or remove items
     * @return {boolean}
     */
    userCanEditOrRemoveItems() {
      return (
        this.isSuperAdmin ||
        (this.isAdmin &&
          (this.userHasPermissionTo("update") ||
            this.userHasPermissionTo("destroy")))
      );
    },

    /**
     * Determines if the user can list the items.
     * @return {boolean}
     */
    canSeeTable() {
      return (
        this.isSuperAdmin || (this.isAdmin && this.userHasPermissionTo("list"))
      );
    },

    /**
     * Determines table headers for client roles
     * {String} value - Column key
     * {String} text - Column title
     * {String} align - Column alignment
     */
    mainHeaders() {
      return [
        { text: this.$i18n.t("Name"), align: "left", value: "name" },
        {
          text: this.$i18n.t("Description"),
          align: "left",
          value: "description"
        },
        { text: this.$i18n.t("Status"), align: "left", value: "status" },
        { text: this.$i18n.t("Access"), align: "left", value: "visible" }
      ];
    }
  },

  mounted() {
    this.getClientRoles();
  },

  methods: {
    ...mapActions(["fetchAllClientRoles", "deleteClientRoleByUuid"]),

    getRoleRowsFromLocalStorage(id) {
      let rows = parseInt(localStorage.getItem(id));
      if (rows) {
        return rows;
      } else {
        return 15;
      }
    },
    setRoleRowsOnLocalStorage(id, rows) {
      localStorage.setItem(id, rows);
    },
    /**
     * Get the list of client roles
     */
    async getClientRoles() {
      try {
        this.roles = await this.fetchAllClientRoles();
      } catch (error) {
        console.error(error);
      } finally {
        this.loading = false;
        this.$nextTick(() =>
          this.updateLocalPagination(this.paginationFromParent)
        );
      }
    },

    /**
     * Push to the page to see the selected client role
     * @param {Object} item - Selected client role
     */
    viewItem(item) {
      /**
       * @property {String} name - Name of the route
       * @property {Object} params - Parameters needed for the route path
       */
      this.$router.push({ name: "client-role", params: { uuid: item.uuid } });
    },

    /**
     * Push to the page to edit the selected client role
     * @param {Object} item - Selected client role
     */
    editItem(item) {
      /**
       * @property {String} name - Name of the route
       * @property {Object} params - Parameters needed for the route path
       */
      this.$router.push({
        name: "client-role-edit",
        params: { uuid: item.uuid }
      });
    },

    /**
     * Sends a request to delete the selected client role and closes the delete confirmation dialog.
     */
    async deleteItem() {
      try {
        this.loadingDelete = true;
        let index = this.roles.indexOf(this.itemToDelete);
        await this.deleteClientRoleByUuid(this.itemToDelete.uuid);
        this.roles.splice(index, 1);
      } catch (error) {
        console.error(error);
      } finally {
        this.deleteDialog = false;
        this.loadingDelete = false;
      }
    }
  }
};
</script>



// WEBPACK FOOTER //
// src/components/SystemManagement/Accounts/ClientRoles/ClientRoleList.vue